// F8: 生成系実行（treasures 再生成・dataCache 版番更新）
import path from 'node:path';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { readJson, writeJson } from './json-utils.mjs';
import { runNpm } from './proc.mjs';
import { auditLog } from './logger.mjs';

function recordOperation(gameId, key, info) {
    updateGameMeta(gameId, (meta) => ({
        ...meta,
        lastOperations: {
            ...(meta.lastOperations ?? {}),
            [key]: { at: new Date().toISOString(), ...info },
        },
    }));
}

export async function runGenerateTreasures(gameId, { onLine = () => {}, signal = null } = {}) {
    const dir = requireGameDir(gameId);
    const r = await runNpm(dir, ['run', 'generate:treasures'], { onLine, signal });
    recordOperation(gameId, 'treasures', { ok: r.code === 0 });
    auditLog('generate-treasures', { gameId, ok: r.code === 0 });
    return { ok: r.code === 0, code: r.code, output: r.output };
}

const todayStamp = () => new Date().toISOString().slice(0, 10);

/** `YYYY-MM-DD-<gameId>-vN` 形式の次版番を提案する。 */
export function proposeNextVersion(current, gameId, suffix = '') {
    const slug = gameId.replaceAll('_', '-') + (suffix ? `-${suffix}` : '');
    const prefix = `${todayStamp()}-${slug}-v`;
    if (typeof current === 'string' && current.startsWith(prefix)) {
        const n = Number.parseInt(current.slice(prefix.length), 10);
        if (Number.isFinite(n)) return `${prefix}${n + 1}`;
    }
    return `${prefix}1`;
}

/**
 * cache-manifest.json の dataCache.version を更新する（公開後の JSON 更新時に必須の工程）。
 * withPlayerModels=true でコアキャラ GLB の playerModelCache.version も更新。
 */
export function bumpDataCacheVersion(gameId, { withPlayerModels = false, dryRun = false } = {}) {
    const dir = requireGameDir(gameId);
    const cmPath = path.join(dir, 'maps/_shared/cache-manifest.json');
    const cm = readJson(cmPath);
    const result = { file: 'maps/_shared/cache-manifest.json', changes: [] };
    if (cm.dataCache) {
        const next = proposeNextVersion(cm.dataCache.version, gameId);
        result.changes.push({ key: 'dataCache.version', from: cm.dataCache.version, to: next });
        cm.dataCache.version = next;
    }
    if (withPlayerModels && cm.playerModelCache) {
        const next = proposeNextVersion(cm.playerModelCache.version, gameId, 'core');
        result.changes.push({ key: 'playerModelCache.version', from: cm.playerModelCache.version, to: next });
        cm.playerModelCache.version = next;
    }
    if (!dryRun && result.changes.length > 0) {
        writeJson(cmPath, cm);
        const at = new Date().toISOString();
        updateGameMeta(gameId, (meta) => ({
            ...meta,
            lastDataCacheBump: at,
            lastOperations: { ...(meta.lastOperations ?? {}), dataCache: { ok: true, at, changes: result.changes.length } },
        }));
        auditLog('bump-data-cache', { gameId, changes: result.changes });
    }
    return result;
}

export async function runBuild(gameId, { onLine = () => {}, signal = null } = {}) {
    const dir = requireGameDir(gameId);
    const r = await runNpm(dir, ['run', 'build'], { onLine, signal });
    recordOperation(gameId, 'build', { ok: r.code === 0 });
    auditLog('build', { gameId, ok: r.code === 0 });
    return { ok: r.code === 0, code: r.code, output: r.output };
}

export async function runNpmCi(gameId, { onLine = () => {} } = {}) {
    const dir = requireGameDir(gameId);
    const r = await runNpm(dir, ['ci'], { onLine });
    recordOperation(gameId, 'npmCi', { ok: r.code === 0 });
    auditLog('npm-ci', { gameId, ok: r.code === 0 });
    return { ok: r.code === 0, code: r.code, output: r.output };
}
